/**
 * Theme store — controls light/dark mode for the entire app.
 *
 * Why Zustand here even though it's tiny state: we want
 *   1. The theme to persist across reloads (localStorage).
 *   2. Components that don't read it directly to still re-render when it changes
 *      (e.g. SVG charts that pick fills from CSS vars on mount).
 *
 * The actual theme switch is just `<html data-theme="...">`. Tailwind selects
 * via that attribute (configured in tailwind.config.ts).
 */
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

/** The two supported modes. "system" follows OS preference at boot. */
export type Theme = "light" | "dark" | "system";

interface ThemeState {
  theme: Theme;
  /** The mode actually applied to <html> (resolves "system" → light/dark). */
  resolved: "light" | "dark";

  setTheme: (t: Theme) => void;
  /** Toggle between explicit light/dark — used by the topbar switch. */
  toggle: () => void;
  /** Recompute `resolved` from `theme` + OS preference. Idempotent. */
  rehydrate: () => void;
}

/** Resolve "system" → the OS-reported value, default "dark" if no media query. */
function resolveSystem(): "light" | "dark" {
  if (typeof window === "undefined") return "dark";
  return window.matchMedia("(prefers-color-scheme: light)").matches
    ? "light"
    : "dark";
}

/** Apply the resolved value to the <html> element so Tailwind picks it up. */
function applyToDom(resolved: "light" | "dark"): void {
  if (typeof document === "undefined") return;
  document.documentElement.dataset.theme = resolved;
}

export const useTheme = create<ThemeState>()(
  persist(
    (set, get) => ({
      theme: "dark",
      resolved: "dark",

      setTheme: (t) => {
        const resolved = t === "system" ? resolveSystem() : t;
        applyToDom(resolved);
        set({ theme: t, resolved });
      },

      toggle: () => {
        const next = get().resolved === "dark" ? "light" : "dark";
        applyToDom(next);
        set({ theme: next, resolved: next });
      },

      rehydrate: () => {
        const t = get().theme;
        const resolved = t === "system" ? resolveSystem() : t;
        applyToDom(resolved);
        set({ resolved });
      },
    }),
    {
      name: "opus.theme",
      // Explicit JSON storage adapter (Zustand v5 requires this wrapper around
      // localStorage so the storage interface is uniform across SSR/jsdom).
      storage: createJSONStorage(() => localStorage),
      // Only the user-chosen value is persisted; `resolved` is derived on boot.
      partialize: (s) => ({ theme: s.theme }),
    },
  ),
);
